import { useState } from 'react'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { EMPTY_MARK, formatAmount, formatDateTime } from '@/lib/venue/format'
import type {
  ReservationBookerContactView,
  ReservationDetailView,
  ReservationFeeView,
  ReservationHistoryView,
} from '@/lib/venue/reservationTypes'
import {
  RESERVATION_STATUS_OPTIONS,
  RESERVATION_STATUS_TONE,
  bookerNameText,
  bookerPhoneText,
  bookerPurgeNoticeText,
} from '@/lib/venue/reservationViewModel'

/**
 * 이력 표의 상태 전이 행(변경전) 라벨 — 서버가 `afterStatusLabel`만 주고 `beforeStatusLabel`은 내려주지
 * 않는 유일한 갭이라, 필터용으로 이미 있는 카탈로그를 표시용으로도 재사용한다(설계 §2 자율 결정 3).
 */
const STATUS_LABEL: Record<string, string> = Object.fromEntries(
  RESERVATION_STATUS_OPTIONS.map((option) => [option.value, option.label])
)

/** 일정 변경 행 앞/뒤를 "{코트} {날짜} {시작}~{종료}" 한 줄로 조립한다. */
function scheduleChangeText(part: {
  courtName: string
  useDate: string
  startTime: string
  endTime: string
}): string {
  return `${part.courtName} ${part.useDate} ${part.startTime}~${part.endTime}`
}

/**
 * 처리 이력 표 한 행의 "처리유형"·"변경전"·"변경후" 텍스트.
 * 🚨 `scheduleChange` 존재 여부로 "일정 변경" 행과 "상태 처리" 행을 가른다(#669 §2-1, §5 체크리스트).
 * `beforeStatus === afterStatus` 인 일정 변경 행을 "의미 없는 행"으로 걸러내지 않는다.
 */
function historyRowText(history: ReservationHistoryView): { type: string; before: string; after: string } {
  if (history.scheduleChange) {
    const { scheduleChange } = history
    return {
      type: '일정 변경',
      before: scheduleChangeText({
        courtName: scheduleChange.beforeCourtName,
        useDate: scheduleChange.beforeUseDate,
        startTime: scheduleChange.beforeStartTime,
        endTime: scheduleChange.beforeEndTime,
      }),
      after: scheduleChangeText({
        courtName: scheduleChange.afterCourtName,
        useDate: scheduleChange.afterUseDate,
        startTime: scheduleChange.afterStartTime,
        endTime: scheduleChange.afterEndTime,
      }),
    }
  }
  return {
    type: history.afterStatusLabel,
    before: STATUS_LABEL[history.beforeStatus] ?? history.beforeStatus,
    after: history.afterStatusLabel,
  }
}

export interface BookerContactState {
  data: ReservationBookerContactView | undefined
  isFetching: boolean
  onRequest: () => void
}

function BookerContactSection({
  reservation,
  contact,
}: {
  reservation: ReservationDetailView['reservation']
  contact: BookerContactState
}) {
  if (!reservation.bookerContactAvailable) {
    return <p className="mt-2 text-xs text-slateish-600">이 예약은 예약자 연락처를 확인할 수 없습니다.</p>
  }

  if (contact.data) {
    if (!contact.data.available) {
      return (
        <p className="mt-2 text-xs text-slateish-600">
          {contact.data.unavailableReasonLabel ?? '연락처를 확인할 수 없습니다.'}
        </p>
      )
    }
    return (
      <a
        href={`tel:${contact.data.telValue}`}
        className="mt-2 inline-block text-sm font-medium text-brand-500 underline"
      >
        {contact.data.maskedPhone} 로 전화 연결
      </a>
    )
  }

  return (
    <Button
      variant="secondary"
      size="sm"
      className="mt-2"
      disabled={contact.isFetching}
      onClick={contact.onRequest}
    >
      {contact.isFetching ? '조회 중' : '예약자 연락'}
    </Button>
  )
}

/** 취소·환불 규정 카드의 "규정 상세 보기" 토글 — 이미 상세 응답에 포함된 티어 표를 펼친다(추가 API 호출 없음). */
function PolicyDetailToggle({ lines }: { lines: ReservationDetailView['cancellation']['policySummaryLines'] }) {
  const [open, setOpen] = useState(false)

  if (lines.length === 0) return null

  return (
    <div>
      <Button variant="ghost" size="sm" className="px-0" onClick={() => setOpen((current) => !current)}>
        규정 상세 보기 {open ? '▲' : '▼'}
      </Button>
      {open ? (
        <table className="mt-2 w-full text-left text-xs text-ink-700">
          <thead>
            <tr className="border-b border-slateish-200 text-slateish-400">
              <th scope="col" className="py-1.5 pr-3 font-medium">
                구간
              </th>
              <th scope="col" className="py-1.5 pr-3 font-medium">
                환불
              </th>
              <th scope="col" className="py-1.5 font-medium">
                수수료
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={`${line.rangeLabel}-${index}`} className="border-b border-slateish-200 last:border-b-0">
                <td className="py-1.5 pr-3">{line.rangeLabel}</td>
                <td className="py-1.5 pr-3">{line.refundLabel}</td>
                <td className="py-1.5">{line.feeLabel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  )
}

/**
 * "결제" 카드의 수수료 상세 토글(#791) — `PolicyDetailToggle`과 동일한 펼침 패턴이다.
 * 🚨 `fee`가 `null`인 예약(전화·현장 예약 등)에서는 이 컴포넌트 자체를 렌더링하지 않는다(호출부에서 분기).
 */
function FeeDetailToggle({ fee }: { fee: ReservationFeeView }) {
  const [open, setOpen] = useState(false)

  return (
    <div>
      <Button variant="ghost" size="sm" className="px-0" onClick={() => setOpen((current) => !current)}>
        수수료 상세 보기 {open ? '▲' : '▼'}
      </Button>
      {open ? (
        <div className="mt-1 space-y-1 text-sm text-ink-700">
          <p>적용 수수료율(결제 시점 기준) {fee.appliedFeeRatePercentLabel}</p>
          <p>수수료 금액 {formatAmount(fee.feeAmount)}</p>
        </div>
      ) : null}
    </div>
  )
}

const HISTORY_COLUMNS = ['일시', '처리유형', '처리자', '변경전', '변경후', '비고']

/** 처리 이력 표 — 요구사항 "일시·처리유형·처리자·변경전·변경후·비고"(#669 §2-1 확장 반영). */
function HistoryTable({ histories }: { histories: ReservationHistoryView[] }) {
  if (histories.length === 0) return <p className="text-sm text-slateish-600">처리 이력 없음</p>

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead>
          <tr className="border-b border-slateish-200 text-xs text-slateish-400">
            {HISTORY_COLUMNS.map((column) => (
              <th key={column} scope="col" className="whitespace-nowrap py-2 pr-4 font-medium">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {histories.map((history) => {
            const row = historyRowText(history)
            return (
              <tr key={history.id} className="border-b border-slateish-200 text-ink-700 last:border-b-0">
                <td className="whitespace-nowrap py-2 pr-4">{formatDateTime(history.occurredAt)}</td>
                <td className="whitespace-nowrap py-2 pr-4">{row.type}</td>
                <td className="whitespace-nowrap py-2 pr-4">{history.actorTypeLabel}</td>
                <td className="py-2 pr-4">{row.before}</td>
                <td className="py-2 pr-4">{row.after}</td>
                <td className="py-2 pr-4 text-slateish-600">{history.reason ?? EMPTY_MARK}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** 예약 상세 5블록(설계 §2-4(3)) — 전부 읽기 전용이다(취소·환불 실행 버튼 없음). */
export function ReservationDetailSections({
  detail,
  contact,
}: {
  detail: ReservationDetailView
  contact: BookerContactState
}) {
  const { reservation, pricing, payment, cancellation, refunds, histories, fee } = detail

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="예약 정보"
          action={<Badge tone={RESERVATION_STATUS_TONE[reservation.status]}>{reservation.statusLabel}</Badge>}
        />
        <CardBody className="space-y-1 text-sm text-ink-700">
          <p>예약번호 {reservation.reservationNo ?? '-'}</p>
          <p>
            {reservation.courtName} · {formatDateTime(reservation.startsAt)} ~{' '}
            {formatDateTime(reservation.endsAt)}
          </p>
          <p>{reservation.sourceLabel}</p>
          <p>
            예약자 {bookerNameText(reservation)} · {bookerPhoneText(reservation)}
          </p>
          {reservation.memo ? <p>메모 {reservation.memo}</p> : null}
          <p className="text-xs text-slateish-600">{bookerPurgeNoticeText(reservation)}</p>
          <BookerContactSection reservation={reservation} contact={contact} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="결제" />
        <CardBody className="space-y-1 text-sm text-ink-700">
          <p>
            {pricing.useDate} {pricing.startTime}~{pricing.endTime} · {pricing.slotCount}칸 ·{' '}
            {formatAmount(pricing.amount)}
          </p>
          <p>
            {payment.paymentMethodLabel ?? '-'} · {payment.paymentStatusLabel}
            {payment.paidAt ? ` · ${formatDateTime(payment.paidAt)}` : ''}
          </p>
          {fee ? (
            <>
              <p className="font-semibold text-ink-900">
                정산 예정 {formatAmount(fee.settlementExpectedAmount)} (수수료 {fee.appliedFeeRatePercentLabel} 차감)
              </p>
              <FeeDetailToggle fee={fee} />
            </>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="취소·환불 규정" />
        <CardBody className="space-y-1 text-sm text-ink-700">
          <p>{cancellation.cancellableUntilLabel}</p>
          <p>
            {cancellation.cancellable ? '취소 가능' : '취소 기한 지남'} · 환불 비율{' '}
            {cancellation.refundRatePercent}% · 예상 환불액 {formatAmount(cancellation.estimatedRefundAmount)}
          </p>
          <p className="text-xs text-slateish-600">{cancellation.policySummaryText}</p>
          {cancellation.additionalNotice ? (
            <p className="text-xs text-slateish-600">{cancellation.additionalNotice}</p>
          ) : null}
          <PolicyDetailToggle lines={cancellation.policySummaryLines} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="환불 내역" />
        <CardBody>
          {refunds.length === 0 ? (
            <p className="text-sm text-slateish-600">환불 내역 없음</p>
          ) : (
            <ul className="space-y-2 text-sm text-ink-700">
              {refunds.map((refund) => (
                <li key={refund.refundId} className="border-b border-slateish-200 pb-2 last:border-b-0">
                  {refund.statusLabel} · {formatAmount(refund.refundAmount)} ·{' '}
                  {formatDateTime(refund.requestedAt)}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="처리 이력" />
        <CardBody>
          <HistoryTable histories={histories} />
        </CardBody>
      </Card>
    </div>
  )
}
